import React from 'react';
// Mocking Link as react-router-dom is not available in a single-file environment
const Link: React.FC<{ to: string, className: string, children: React.ReactNode }> = ({ children, className }) => <a href="#" className={className}>{children}</a>;
// Using inline SVGs for lucide-react icons as external packages are not guaranteed to be available
const Icon = ({ size = 24, className = '', children }: { size?: number, className?: string, children: React.ReactNode }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        {children}
    </svg>
);
const Mail: React.FC<{ size?: number, className?: string }> = ({ size, className }) => (
    <Icon size={size} className={className}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></Icon>
);
const Phone: React.FC<{ size?: number, className?: string }> = ({ size, className }) => (
    <Icon size={size} className={className}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72v4.71a2 2 0 0 1-.5 1.26l-2.17 2.18a1.5 1.5 0 0 0 0 2.12l5.12 5.12a1.5 1.5 0 0 0 2.12 0l2.18-2.17a2 2 0 0 1 1.26-.5h4.71A2 2 0 0 1 22 16.92z"></path></Icon>
);
const MapPin: React.FC<{ size?: number, className?: string }> = ({ size, className }) => (
    <Icon size={size} className={className}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></Icon>
);
const Linkedin: React.FC<{ size?: number, className?: string }> = ({ size, className }) => (
    <Icon size={size} className={className}><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect width="4" height="12" x="2" y="9"></rect><circle cx="4" cy="4" r="2"></circle></Icon>
);
const Instagram: React.FC<{ size?: number, className?: string }> = ({ size, className }) => (
    <Icon size={size} className={className}><rect width="20" height="20" x="2" y="2" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"></line></Icon>
);

const Footer: React.FC = () => {
  return (
    // FIX 1: Removed bottom padding (pb-*) from the main footer to be controlled by the bottom bar.
    <footer className="bg-[#1a1c1b] border-t border-[#3f4816] pt-6 sm:pt-8 md:pt-12 lg:pt-16 pb-0" style={{ marginBottom: 0, marginTop: 0 }}>
      <div className="max-w-[87.5rem] mx-auto px-4 sm:px-6 md:px-8 lg:px-10">
        
        {/* Main Grid: Stacks on mobile, four columns on desktop */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-4 md:gap-8 lg:gap-12 mb-6 sm:mb-8 md:mb-10 lg:mb-12">
          
          {/* Company Info (Takes 2/4 columns on larger screens) */}
          <div className="col-span-1 sm:col-span-2 lg:col-span-2">
            <h3 className="text-[#d9fb06] font-bold text-xl sm:text-2xl md:text-3xl mb-2 sm:mb-3 md:mb-4">Kapoor & Sons</h3>
            <p className="text-[#888680] mb-3 sm:mb-4 max-w-md text-sm sm:text-base leading-relaxed">
              Your trusted import-export partner, committed to facilitating seamless global trade connections and building lasting business relationships.
            </p>
            <div className="flex space-x-3 sm:space-x-4">
              <a href="https://www.linkedin.com/in/kapoor-and-sons-73969a385?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app" className="text-[#888680] hover:text-[#d9fb06] transition-colors p-1" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
                {/* Responsive icon size */}
                <Linkedin size={24} className="w-5 h-5 sm:w-6 sm:h-6" />
              </a>
              <a href="https://www.instagram.com/kapoor_sons_exporter2025?igsh=MTBveG5tNWxmaHZibQ==" className="text-[#888680] hover:text-[#d9fb06] transition-colors p-1" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
                {/* Responsive icon size */}
                <Instagram size={24} className="w-5 h-5 sm:w-6 sm:h-6" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="col-span-1">
            <h4 className="text-[#d9fb06] font-semibold mb-3 sm:mb-4 text-base sm:text-lg">Quick Links</h4>
            <ul className="space-y-2 sm:space-y-3">
              <li>
                <Link to="/" className="text-[#888680] hover:text-[#d9fb06] transition-colors text-sm sm:text-base block py-1">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-[#888680] hover:text-[#d9fb06] transition-colors text-sm sm:text-base block py-1">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/products" className="text-[#888680] hover:text-[#d9fb06] transition-colors text-sm sm:text-base block py-1">
                  Products
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-[#888680] hover:text-[#d9fb06] transition-colors text-sm sm:text-base block py-1">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="col-span-1">
            <h4 className="text-[#d9fb06] font-semibold mb-3 sm:mb-4 text-base sm:text-lg">Contact Us</h4>
            <ul className="space-y-3 sm:space-y-4">
              {/* Address: Text size and icon size adjusted for smaller screens */}
              <li className="flex items-start space-x-2 sm:space-x-3 text-[#888680]">
                <MapPin size={18} className="mt-1 flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-sm leading-relaxed">
                  3277, Omkar Niwas, Opp. Ramdevpir Mandir, Gayatri Nagar Society, At:- Tarapur, Tal:- Tarapur, Dist: Anand, Pin-Code:- 388 180
                </span>
              </li>
              {/* Phone: Text size and icon size adjusted */}
              <li className="flex items-center space-x-2 sm:space-x-3 text-[#888680]">
                <Phone size={18} className="flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-sm">+91 6355 600 701</span>
              </li>
              {/* Email: Text size and icon size adjusted, break-all ensures long email doesn't overflow */}
              <li className="flex items-start space-x-2 sm:space-x-3 text-[#888680]">
                <Mail size={18} className="mt-1 flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-sm break-all">kapoorandsonsexporter2025@gmail.com</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar - Contains the line that was potentially missing */}
        {/* FIX 2: Added explicit bottom padding (pb-*) to the copyright div for guaranteed clearance on all devices. */}
        <div className="pt-4 sm:pt-6 md:pt-8 pb-4 sm:pb-6 md:pb-8 border-t border-[#3f4816] text-center">
          <p className="text-[#888680] text-xs sm:text-sm px-2 sm:px-4 leading-relaxed">
            {/* Using 2025 to match the original image/email context */}
            © 2025 Kapoor & Sons Import Export. All rights reserved.
            {/* Best Practice: Use {new Date().getFullYear()} for dynamic year */}
          </p>
          <div className="mt-3 flex justify-center px-2 sm:px-4">
            <span className="inline-flex flex-wrap items-center justify-center gap-2 rounded-full border border-[#3f4816] bg-[#242321] px-4 py-2 text-xs sm:text-sm text-[#d9fb06] shadow-sm">
              <span className="tracking-wide uppercase text-[0.7rem] sm:text-xs">Developed by</span>
              <a
                href="https://kavaiyatech.com/"
                className="inline-flex items-center gap-2 font-semibold bg-gradient-to-r from-[#00B4FF] via-[#7B61FF] to-[#FF00FF] bg-clip-text text-transparent hover:opacity-80 transition-opacity"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="/Kavaiyatech_Logo.jpeg"
                  alt="Kavaiya Technologies logo"
                  className="h-5 w-5 rounded-full object-cover sm:h-6 sm:w-6"
                  loading="lazy"
                  decoding="async"
                />
                Kavaiya Technologies
              </a>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App component to render the Footer
const App: React.FC = () => {
    return (
        <div className="">
            
            <Footer />
        </div>
    );
};

export default App;
